import type { BaseFilter } from "@circle-react-shared/MemberFilters/types";
import type { ProfileFieldBaseFilter } from "@circle-react-shared/MemberFilters/types";
import type { ApiError } from "@circle-react/config/CustomErrors";
import { usePunditUserContext } from "@circle-react/contexts/punditUserContext";
import {
  reactQueryDelete,
  reactQueryPost,
  reactQueryPut,
} from "@circle-react/helpers/backendRequestHelpers";
import { internalApi } from "@circle-react/helpers/urlHelpers";
import { useCommunityInboxStore } from "@circle-react/hooks/chatsV2/store";
import {
  aiAgentInvalidations,
  aiAgentQueries,
} from "@circle-react/lib/queries/aiAgents";
import { queryClient } from "@circle-react/providers/QueryClient/reactQueryConfig";
import { useToast } from "@circleco/compass/context/CompassContext";
import { flatten, omit, pick, values } from "lodash";
import { useMutation, useQuery } from "react-query";

import { t } from "@/i18n-js/instance";
import { avatarSitePageInvalidations } from "@/react/lib/queries/avatarSitePages";

const FLASH_NOTICE_TEST_ID = { "data-testid": "flash-notice" } as const;

const SUCCESS_TOAST = {
  ...FLASH_NOTICE_TEST_ID,
  variant: "primary",
  icon: "circle-check-filled",
  iconColor: "success",
} as const;

const ERROR_TOAST = {
  ...FLASH_NOTICE_TEST_ID,
  variant: "destructive",
  icon: "triangle-exclamation-filled",
  iconColor: "danger",
} as const;

export interface AgentProp {
  allow_direct_messages: boolean;
  allow_search_conversations: boolean;
  avatar_url: string;
  bio: object;
  conversations?: number;
  created_at: string;
  enabled: boolean;
  id: number;
  members_filters?: Array<BaseFilter | ProfileFieldBaseFilter>;
  members_filters_tooltip?: string;
  messages?: number;
  modified_on?: string;
  name: string;
  pause_ai_conditions: string | null;
  pause_ai_handoff_message: any;
  pause_ai_keywords: Array<string>;
  plain_text_bio?: string;
  posts_count: number;
  purpose: string;
  send_welcome_message: boolean;
  site_page_ids?: number[];
  snippet_ids?: number[];
  snippets_count: number;
  space_ids?: number[];
  suggested_questions?: string[];
  tone: "normal" | "friendly" | "short-and-sweet" | "descriptive";
  updated_at: string;
  use_all_kb_posts: boolean;
  use_community_knowledge: boolean;
  use_custom_snippets: boolean;
  use_member_directory: boolean;
  use_platform_support: boolean;
  use_web_search: boolean;
  welcome_message: object;
}

export type AgentModalTarget = Pick<AgentProp, "id" | "name">;

export interface UpdateAgentRequest {
  allow_direct_messages?: boolean;
  allow_search_conversations?: boolean;
  avatar?: string;
  bio?: object;
  enabled?: boolean;
  id?: number;
  members_filters?: Array<object>;
  name?: string;
  pause_ai_conditions?: string | null;
  pause_ai_handoff_message?: any;
  pause_ai_keywords?: Array<string>;
  purpose?: string;
  send_welcome_message?: boolean;
  site_page_ids?: number[];
  snippet_ids?: number[];
  space_ids?: number[];
  suggested_questions?: string[];
  tone?: "normal" | "friendly" | "short-and-sweet" | "descriptive";
  use_member_directory?: boolean;
  use_platform_support?: boolean;
  use_web_search?: boolean;
  welcome_message?: object;
}

export const useIndexAgents = ({
  currentPage,
  perPage = 10,
  enabled = true,
}: {
  currentPage: number;
  perPage?: number;
  enabled?: boolean;
}) =>
  useQuery({
    ...aiAgentQueries.list({ page: currentPage, perPage }),
    enabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

export const useShowAgent = ({ id }: { id: number }) =>
  useQuery(aiAgentQueries.detail(id));

export const useCreateAgent = () => {
  const toast = useToast();

  return useMutation<AgentProp, Error, void>({
    mutationFn: async () => {
      const response: AgentProp = await reactQueryPost(
        internalApi.communityBot.agents.create(),
      );
      return response;
    },
    onSuccess: () => {
      void aiAgentInvalidations.all();
      toast.add({ title: t("changes_saved"), ...SUCCESS_TOAST });
    },
  });
};

interface UseUpdateAgentParams {
  id: number;
  shouldKeepFormVersion?: boolean;
}

export const useUpdateAgent = ({
  id,
  shouldKeepFormVersion = false,
}: UseUpdateAgentParams) => {
  const toast = useToast();
  const { refetch: refetchPundit } = usePunditUserContext();
  const { updateAgentChats } = useCommunityInboxStore();

  return useMutation<AgentProp, Error, UpdateAgentRequest>({
    mutationFn: (data: UpdateAgentRequest) => {
      const isEnabledUpdate =
        Object.keys(data).length === 1 && "enabled" in data;

      return reactQueryPut(internalApi.communityBot.agents.update({ id }), {
        community_bot_profile: isEnabledUpdate
          ? { enabled: data.enabled }
          : omit(data, "enabled"),
      });
    },
    onSuccess: (updatedAgent, variables) => {
      // Inline edits in the Avatars v2 preview autosave while the BotBuilder
      // form holds unsaved edits. Patching the cache instead of refetching
      // keeps updated_at, which keys the form, from remounting it.
      if (shouldKeepFormVersion) {
        queryClient.setQueryData<AgentProp>(
          aiAgentQueries.detail(id).queryKey,
          agent =>
            agent && {
              ...agent,
              ...pick(updatedAgent, [
                ...Object.keys(variables),
                "plain_text_bio",
              ]),
            },
        );
        void aiAgentInvalidations.list();
      } else {
        void aiAgentInvalidations.all();
      }
      if (variables.site_page_ids !== undefined) {
        void avatarSitePageInvalidations.selected();
      }
      // Enabling/disabling an agent only invalidates agent queries; inbox chats stay
      // stale until UpdateDisabledStatusJob broadcasts over websocket. Patch the store
      // now (optimistic update) so BotChatRoom (broadcast banner, composer) and the
      // sidebar update immediately.
      if (typeof variables.enabled === "boolean") {
        updateAgentChats(id, variables.enabled);
      }
      toast.add({ title: t("changes_saved"), ...SUCCESS_TOAST });
    },
    onError: async (exception: any) => {
      await refetchPundit();
      if (exception?.body?.error_details) {
        const details = values(
          omit(exception.body.error_details, "message", "success"),
        );
        toast.add({ title: flatten(details).join(", "), ...ERROR_TOAST });
      } else {
        toast.add({ title: t("request_failure_message"), ...ERROR_TOAST });
      }
    },
  });
};

export interface AgentDeleteBlockingWorkflow {
  id: string;
  name: string | null;
  status: string;
}

export const blockingWorkflowsFromError = (
  error: ApiError | null,
): AgentDeleteBlockingWorkflow[] => {
  const workflows: unknown = error?.body?.workflows;
  return Array.isArray(workflows)
    ? (workflows as AgentDeleteBlockingWorkflow[])
    : [];
};

export const useDeleteAgent = ({ id }: { id: number }) => {
  const toast = useToast();

  return useMutation<void, ApiError, void>({
    mutationFn: () =>
      reactQueryDelete<void, object>(
        internalApi.communityBot.agents.destroy({ id }),
      ),
    onSuccess: () => {
      void aiAgentInvalidations.deleted(id);
      void queryClient.invalidateQueries({
        queryKey: ["community-inbox", "chat-bot", "profiles"],
      });
      toast.add({ title: t("avatars.delete_modal.deleted"), ...SUCCESS_TOAST });
    },
    onError: error => {
      if (blockingWorkflowsFromError(error).length === 0) {
        toast.add({ title: t("request_failure_message"), ...ERROR_TOAST });
      }
    },
  });
};
